import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import SydModal from '../../../../commonDesign/SydModal'
import SaveCancelButtons from '../../../../molecules/SaveCancelButtons'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import { useSetBranding } from '../../../../../api/abundanceEngine'
import SydSwitch from '../../../../commonDesign/SydSwitch'
import { modalStyles } from './brandModalStyles'
import { useBrandImageManager } from './useBrandImageManager'
import { BrandImageModals } from './BrandImageModals'
import { ImageUrlField } from './ImageUrlField'

const BrandModal = ({ open, onClose }) => {
  const classes = modalStyles()
  const { mutateAsync: addBrand, isLoading } = useSetBranding()
  const [error, setError] = useState(null)
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      brandKey: '',
      firmName: '',
      tagLine: '',
      logoUrl: '',
      faviconUrl: '',
      useDefaultAvatar: false,
      avatarUrl: ''
    }
  })
  const useDefaultAvatar = form.watch('useDefaultAvatar')
  const { openAddModal, handleShowImageList, imageModalProps } = useBrandImageManager(form)
  const onSubmit = useCallback(async (data) => {
    setError(null)
    try {
      const payload = { ...data }
      if (!payload.useDefaultAvatar) {
        delete payload.avatarUrl
      }
      const result = await addBrand(payload)
      if (result && result.success !== false) {
        form.reset()
        onClose()
      }
    } catch (err) {
      console.error('Error saving branding:', err)
      setError(err?.response?.data?.message || err?.message || 'An error occurred while saving')
    }
  }, [onClose, addBrand, form])
  return (
    <>
      <SydModal open={open} onClose={onClose} title='Add Branding'>
        <div className={classes.inputRow}>
          <Controller
            name='brandKey'
            control={form.control}
            rules={{ required: { value: true, message: 'Brand key is required' } }}
            render={({ field, fieldState }) => (
              <SydLabel label='Brand Key' error={hookFormErrorAdapter(form, fieldState)}>
                <SydInput {...field} error={fieldState.error} />
              </SydLabel>
            )}
          />
          <Controller
            name='firmName'
            control={form.control}
            rules={{ required: { value: true, message: 'Firm name is required' } }}
            render={({ field, fieldState }) => (
              <SydLabel label='Firm Name' error={hookFormErrorAdapter(form, fieldState)}>
                <SydInput {...field} error={fieldState.error} />
              </SydLabel>
            )}
          />
        </div>
        <div className={classes.inputRow}>
          <Controller
            name='tagLine'
            control={form.control}
            rules={{ required: { value: true, message: 'Tag line is required' } }}
            render={({ field, fieldState }) => (
              <SydLabel label='Tag Line' error={hookFormErrorAdapter(form, fieldState)} className={classes.formLabel}>
                <SydInput {...field} error={fieldState.error} />
              </SydLabel>
            )}
          />
        </div>
        <ImageUrlField
          name='logoUrl'
          label='Logo URL'
          form={form}
          onAddImage={openAddModal}
          onSelectImage={handleShowImageList}
        />
        <ImageUrlField
          name='faviconUrl'
          label='Favicon URL'
          form={form}
          onAddImage={openAddModal}
          onSelectImage={handleShowImageList}
        />
        <div className={classes.inputRow}>
          <Controller
            name='useDefaultAvatar'
            control={form.control}
            render={({ field }) => (
              <SydLabel label='Use Default Avatar'>
                <SydSwitch
                  name={field.name}
                  value={field.value}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  onBlur={field.onBlur}
                />
              </SydLabel>
            )}
          />
        </div>
        {useDefaultAvatar && (
          <ImageUrlField
            name='avatarUrl'
            label='Default Avatar URL'
            form={form}
            onAddImage={openAddModal}
            onSelectImage={handleShowImageList}
          />)}
        {error && <div className={classes.errorMessage}>{error}</div>}
        <div className={classes.buttonRow}>
          <SaveCancelButtons
            onSave={form.handleSubmit(onSubmit)}
            onCancel={onClose}
            isSubmitting={isLoading}
          />
        </div>
      </SydModal>
      <BrandImageModals {...imageModalProps} />
    </>
  )
}

BrandModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func
}

export default BrandModal
